/*
 * Approved copy constants for the Governance Tile · Judge column.
 *
 * Every string is word-for-word from mockup v8.8.12 lines 941-1114 and
 * carries [COPY-APPROVED: governance-tile-v8] (2026-07-24).
 *
 * Data-driven port: each parent-row group and each metric group is a
 * plain data object consumed by the JudgeColumn sub-components. Static
 * mockup estimate values (15, 9, 8, 10, 8, 12, 8, 7, 7, 7, 9, 6.3 s,
 * etc.) are NOT re-exposed here — the renderer binds each row either
 * to a live endpoint field or to the em-dash pending marker (attestation
 * -honesty rule · anchor GB-013 · § V.6).
 */

export const JUDGE_SCORECARD_TITLE = 'How the Judge is going';
export const JUDGE_SCORECARD_ICON = '\u2696'; // scales of justice
export const JUDGE_SUBTITLE =
  'Verifies every task against \u00A7 V.6 sub-topics \u00B7 pass requires all sub-parameters understood + evaluated';

// Live-computed headline copy.  When tasks_total is null (summary
// still loading) both halves show em dash.  When tasks_total is 0
// the sub-line collapses to the "no turns logged" state so the
// tile can never render a stale "N of 48" phrase (operator directive
// 2026-07-24 · 48 was last session's denominator, not this session's).
export const JUDGE_HEADLINE_SUB_REST_PREFIX =
  ' \u00B7 Judge must judge every task the operator gives me at the sub-topic level';
export const JUDGE_HEADLINE_SUB_REST_TAIL =
  ' tasks (either Judge did not run end-to-end or Judge ran but the operator caught it wrong)';
export const JUDGE_HEADLINE_SUB_ZERO = 'No turns logged in this session yet.';
export const JUDGE_STATUS_ZERO = 'Awaiting first turn';
export const JUDGE_STATUS_UNDERPERFORMING = 'Underperforming';
export const JUDGE_STATUS_ON_TRACK = 'On track';

export const JUDGE_SUBTOPIC_SECTION_LABEL = 'Sub-topic Judgements per task (\u00A7 V.6)';
export const JUDGE_SUBTOPIC_SECTION_TOOLTIP =
  'Sub-topic Judgements. Each of the tasks in this session requires the Judge to make TWO parent-level Judgements: (1) Coding Best Practice \u2014 Judge must understand and evaluate all six C1\u2013C6 sub-parameters \u00B7 (2) Quality Assurance Best Practice \u2014 Judge must understand and evaluate all five Q1\u2013Q5 sub-parameters. A parent-row Judgement passes on a task only when every sub-parameter beneath it has been evaluated.';

export const JUDGE_PARENT_GROUPS = [
  {
    key: 'coding',
    parent: 'Coding Best Practice Judgement (parent \u00B7 \u00A7 V.6.a)',
    tooltip:
      'Coding Best Practice Judgement \u2014 parent row. The Judge passes this row on a task only when it has understood and evaluated ALL six sub-parameters underneath: C1 Files \u2264 500 lines each \u00B7 C2 Domain-Driven Design \u00B7 C3 Every Agent as a Product \u00B7 C4 QA in Every Layer \u00B7 C5 Full Observability \u00B7 C6 Version Everything.',
    borderColour: 'border-l-cyan-500',
    subChecks: [
      { label: 'C1 \u00B7 Files \u2264 500 lines each \u00B7 Judge evaluated' },
      { label: 'C2 \u00B7 Domain-Driven Design \u00B7 Judge evaluated' },
      { label: 'C3 \u00B7 Every Agent as a Product \u00B7 Judge evaluated' },
      { label: 'C4 \u00B7 QA in Every Layer \u00B7 Judge evaluated' },
      { label: 'C5 \u00B7 Full Observability \u00B7 Judge evaluated' },
      { label: 'C6 \u00B7 Version Everything \u00B7 Judge evaluated' },
    ],
  },
  {
    key: 'qa',
    parent: 'Quality Assurance Best Practice Judgement (parent \u00B7 \u00A7 V.6.b)',
    tooltip:
      'Quality Assurance Best Practice Judgement \u2014 parent row. The Judge passes this row on a task only when it has understood and evaluated ALL five sub-parameters underneath: Q1 Multi-Layer QA \u00B7 Q2 Automated Regression Testing \u00B7 Q3 End-to-End Pipeline Testing \u00B7 Q4 Continuous Security Testing \u00B7 Q5 Quality Metrics + Release Gates.',
    borderColour: 'border-l-violet-500',
    subChecks: [
      { label: 'Q1 \u00B7 Multi-Layer QA \u00B7 Judge evaluated' },
      { label: 'Q2 \u00B7 Automated Regression Testing \u00B7 Judge evaluated' },
      { label: 'Q3 \u00B7 End-to-End Pipeline Testing \u00B7 Judge evaluated' },
      { label: 'Q4 \u00B7 Continuous Security Testing \u00B7 Judge evaluated' },
      { label: 'Q5 \u00B7 Quality Metrics + Release Gates \u00B7 Judge evaluated' },
    ],
  },
  {
    key: 'judgess',
    parent: 'Judge \u00B7 Session Start Ritual (parent \u00B7 new)',
    tooltip:
      'Judge \u00B7 Session Start Ritual \u2014 parent row (new). Operator rule: "If the operator gives a task, the Judge automatically gets a task too. Before either can run, both must have read and internalised SPD_GOVERNANCE + Mandatory Governance Document." Tracks whether the Judge itself has performed its own T-SS ritual for the session.',
    borderColour: 'border-l-fuchsia-500',
    subChecks: [
      { label: 'SS.J1 \u00B7 SPD_GOVERNANCE.md \u00B7 Judge read + internalised' },
      { label: 'SS.J2 \u00B7 Mandatory Governance Document (MGD) \u00B7 Judge read + internalised' },
      { label: 'SS.J3 \u00B7 Judge + E1 co-read confirmation' },
    ],
  },
];

export const JUDGE_BLOCKING_GATE_LABEL =
  'Both parent rows judged end-to-end (\u00A7 V.6.c blocking gate)';
export const JUDGE_BLOCKING_GATE_TOOLTIP =
  'Both judged end-to-end. A task counts here only when BOTH the Coding parent AND the QA parent were fully judged (all 11 sub-parameters evaluated). This is the headline Judge score. \u00A7 V.6.c blocking rule applies: task fails until both parents pass with all sub-parameters green.';

export const JUDGE_FALSE_NEG_LABEL =
  'Judge False Negative caught by operator this reply \u00B7 IJ-033 chart direction';
export const JUDGE_FALSE_NEG_TOOLTIP =
  'IJ-033 chart \u00B7 Judge False Negative. The Judge returned PASS \u00B7 green \u00B7 plain-english ok on the chart plan in IJ-033. Operator opened the render and reported the plan was wrong. Class-of-failure: Judge did not cross-check the plain-English description against an operator-supplied visual reference. Anchor: GB-019.';

export const JUDGE_INVOCATION_SECTION_LABEL = 'Judge invocation discipline';
export const JUDGE_INVOCATION_ROWS = [
  {
    key: 'never_ran',
    label: 'Plans where Judge never ran (self-judged)',
    tooltip:
      'Count of entries in INTENT_LEDGER with a PASS verdict but an empty JUDGE REASONING field, minus grandfathered entries (IJ-027 anchor).',
  },
  {
    key: 'manual_overrides',
    label: 'Manual overrides used',
    tooltip:
      'Count of entries carrying the [MANUAL-VERDICT-APPROVED: IJ-###] override token. Overrides bypass the Judge and must remain rare.',
  },
  {
    key: 'ban_armed_since',
    label: 'Ban on self-judging armed since',
    tooltip:
      'Timestamp at which the run_all_gates self-judge ban was armed (SPD v5.91 \u00B7 IJ-028).',
  },
  {
    key: 'avg_check_time',
    label: 'Average Judge check time',
    tooltip:
      'Median wall-clock seconds between task submission and Judge verdict emit today.',
  },
];
